import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import { Plus, Calendar as CalendarIcon, Trash2 } from 'lucide-react';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { format } from 'date-fns';

const TaskManagement = () => {
  const [tasks, setTasks] = useState([]);
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    deadline: '',
    priority: 'medium',
    batch_id: '',
    student_ids: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [tasksRes, studentsRes, batchesRes] = await Promise.all([
        axios.get(`${API}/tasks`),
        axios.get(`${API}/users?role=student`),
        axios.get(`${API}/batches`)
      ]);
      setTasks(tasksRes.data);
      setStudents(studentsRes.data);
      setBatches(batchesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/tasks`, {
        ...formData,
        deadline: selectedDate.toISOString()
      });
      toast.success('Task assigned successfully');
      setIsDialogOpen(false);
      setFormData({ title: '', description: '', deadline: '', priority: 'medium', batch_id: '', student_ids: [] });
      setSelectedDate(new Date());
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign task');
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      toast.success('Task deleted successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const filteredStudents = formData.batch_id
    ? students.filter(s => s.batch_id === formData.batch_id)
    : students;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="task-management">
      <Card className="p-6 bg-white shadow-lg border-0">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Task Management</h3>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="flex items-center space-x-2 bg-gradient-to-r from-emerald-500 to-teal-600" data-testid="add-task-button">
                <Plus className="w-4 h-4" />
                <span>Assign Task</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl" data-testid="task-dialog">
              <DialogHeader>
                <DialogTitle>Assign New Task</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="title">Task Title</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    data-testid="task-title-input"
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    required
                    data-testid="task-description-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Deadline</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left" data-testid="task-deadline-button">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(selectedDate, 'PPP')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar mode="single" selected={selectedDate} onSelect={setSelectedDate} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label htmlFor="priority">Priority</Label>
                    <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                      <SelectTrigger data-testid="task-priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="batch">Select Batch</Label>
                  <Select value={formData.batch_id} onValueChange={(value) => setFormData({ ...formData, batch_id: value, student_ids: [] })}>
                    <SelectTrigger data-testid="task-batch-select">
                      <SelectValue placeholder="Select batch" />
                    </SelectTrigger>
                    <SelectContent>
                      {batches.map(batch => (
                        <SelectItem key={batch.id} value={batch.id}>{batch.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {formData.batch_id && (
                  <div>
                    <Label>Select Students</Label>
                    <div className="border rounded-lg p-3 max-h-48 overflow-y-auto">
                      {filteredStudents.map(student => (
                        <label key={student.id} className="flex items-center space-x-2 py-2 hover:bg-gray-50 px-2 rounded">
                          <input
                            type="checkbox"
                            checked={formData.student_ids.includes(student.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({ ...formData, student_ids: [...formData.student_ids, student.id] });
                              } else {
                                setFormData({ ...formData, student_ids: formData.student_ids.filter(id => id !== student.id) });
                              }
                            }}
                            className="rounded"
                            data-testid={`student-checkbox-${student.id}`}
                          />
                          <span className="text-sm">{student.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <Button type="submit" className="w-full" disabled={formData.student_ids.length === 0} data-testid="submit-task-button">
                  Assign Task
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="space-y-3">
          {tasks.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No tasks assigned yet</p>
          ) : (
            tasks.map(task => (
              <Card key={task.id} className="p-4 border-2 hover:shadow-md transition-shadow" data-testid={`task-${task.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="font-semibold text-gray-900">{task.title}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        task.priority === 'high' ? 'bg-red-100 text-red-700' :
                        task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {task.priority}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        task.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                      }`}>
                        {task.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{task.description}</p>
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span>Deadline: {new Date(task.deadline).toLocaleDateString()}</span>
                      <span>Students: {task.student_ids.length}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(task.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded ml-4"
                    data-testid={`delete-task-${task.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </Card>
            ))
          )}
        </div>
      </Card>
    </div>
  );
};

export default TaskManagement;